import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { RequeteRecherche, Segment } from "@sfs/contracts";
import { useDebounce } from "@shared/hooks/useDebounce";
import { useTitre } from "@shared/hooks/useTitre";
import { useFacettes, useRecherche } from "./hooks";
import { rechercheApi } from "./api";
import { PanneauFacettes } from "./components/PanneauFacettes";
import { LigneResultat } from "./components/LigneResultat";

export function PageRecherche() {
  useTitre("Recherche");
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState<"exact" | "similarite">("exact");
  const [filtres, setFiltres] = useState<Partial<RequeteRecherche>>({
    // aucun segment coché au départ : un tableau vide n'est pas envoyé comme
    // filtre (voir recherche.sql.ts), donc les trois segments s'affichent
    // tant que l'utilisateur n'en coche pas un lui-même.
    segments: [] as Segment[], scoreMin: 0, codesNaf: [], departements: [], limite: 25
  });
  const [selection, setSelection] = useState<Set<string>>(new Set());

  const qRetardee = useDebounce(q, 300);
  const requete = useMemo(() => ({ ...filtres, q: qRetardee || undefined, mode }), [filtres, qRetardee, mode]);

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useRecherche(requete);
  const { data: facettes } = useFacettes();

  const lignes = data?.pages.flatMap(p => p.resultats) ?? [];
  const premiere = data?.pages[0];

  function basculer(id: string) {
    setSelection(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  return (
    <div className="rech">
      <PanneauFacettes filtres={filtres} setFiltres={setFiltres} facettes={facettes} />

      <section className="resultats">
        <div className="barre">
          <input type="search" value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Nom, secteur, ville, ou description libre…" aria-label="Recherche" />
          <div className="modes" role="group" aria-label="Mode de recherche">
            <button aria-pressed={mode === "exact"} onClick={() => setMode("exact")}>Exacte</button>
            <button aria-pressed={mode === "similarite"} onClick={() => setMode("similarite")}>Par similarité</button>
          </div>
        </div>

        {isLoading && <p className="etat">Recherche en cours…</p>}

        {isError && (
          <div className="panel">
            <p><strong>La recherche n'a pas abouti.</strong></p>
            <p className="mut">{(error as Error).message}</p>
          </div>
        )}

        {premiere && (
          <div className="meta">
            <span><strong>{premiere.totalExact ? "" : "≈ "}{premiere.total.toLocaleString("fr-FR")}</strong> résultats</span>
            <span>{premiere.dureeMs} ms</span>
            <span>{premiere.mode === "exact" ? "index PostgreSQL" : "recherche vectorielle"}</span>
            <span style={{ marginLeft: "auto" }}>Sélection : {selection.size}</span>
            <button className="btn ghost sm" disabled={!selection.size}
                    onClick={() => rechercheApi.exporter([...selection])}>Exporter CSV</button>
          </div>
        )}

        {premiere && lignes.length === 0 && (
          <div className="panel">
            <p><strong>Aucun prospect ne correspond.</strong></p>
            <p className="mut">Le filtre le plus restrictif est probablement le score minimum ou le segment.</p>
            <button className="btn ghost sm"
                    onClick={() => setFiltres({ segments: ["A", "B", "C"], scoreMin: 0, codesNaf: [], departements: [], limite: 25 })}>
              Réinitialiser les filtres
            </button>
          </div>
        )}

        {lignes.map(l => (
          <LigneResultat key={l.id} ligne={l}
                         selectionne={selection.has(l.id)}
                         onSelection={() => basculer(l.id)}
                         onOuvrir={() => navigate(`/prospects/${l.id}`)} />
        ))}

        {hasNextPage && (
          <button className="btn ghost" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? "Chargement…" : "⌄ Charger 25 résultats de plus"}
          </button>
        )}
      </section>
    </div>
  );
}
